/* eslint-disable jsx-a11y/media-has-caption */
import { Button, Col, Modal, Row } from 'antd'
import moment from 'moment'
import { FC, useState } from 'react'
import { globalConfirm } from 'components/elements/global'
import { TModalState } from 'types'
import FubImage from '../images/followupboss.svg'
import { FubData } from '../types'
import { AddFubIntergrationModal } from './AddFubIntergrationModal'
import {
 useFubSyncMutation,
 useFubTestConnectionMutation,
 useUnlinkFubMutation,
} from '../api'

const initModalState: TModalState<FubData | null> = {
 mode: 'ADD',
 data: null,
 show: false,
}

const syncDirectionLabel: Record<string, string> = {
 twoway: '⇄ Two-way sync',
 push: '→ AgentRoof → Follow Up Boss',
 pull: '← Follow Up Boss → AgentRoof',
}

const FubIntergrationModal: FC<{
 show?: boolean
 onCancel?: () => void
 data?: FubData
}> = ({ onCancel, show, data }) => {
 const [modalState, setModalState] = useState<TModalState>(initModalState)
 const [unlinkFub] = useUnlinkFubMutation()
 const [testConnection, { isLoading: isTesting }] =
  useFubTestConnectionMutation()
 const [sync, { isLoading: isSyncing }] = useFubSyncMutation()

 const handleDisconnect = () => {
  globalConfirm({
   content: 'Are you sure you want to disconnect Follow Up Boss?',
   onOk: async () => {
    await unlinkFub().unwrap()
   },
  })
 }

 const handleTestConnection = async () => {
  try {
   await testConnection().unwrap()
  } catch (error) {
   console.log(error)
  }
 }

 const handleSync = async () => {
  try {
   await sync().unwrap()
  } catch (error) {
   console.log(error)
  }
 }

 return (
  <Modal footer={null} visible={show} onCancel={onCancel} width="100%">
   <div className="intgr-modal">
    <div className="intgr-modal-top">
     <div className="intgr-modal-lgo">
      <img
       className="int-card-new-img int-card-new-img-fub mt-4 mb-3"
       src={FubImage}
       alt=""
      />
      <p>
       Seamlessly connect your Follow Up Boss account to AgentRoof CRM to sync
       leads and contacts directly.
      </p>
     </div>
     <div className="intgr-modal-content">
      <Row style={{ flex: 1 }}>
       <Col flex={1}>
        {data?.connected ? (
         <div className="p-4">
          <h2>Follow Up Boss Account Details</h2>
          <div
           style={{
            display: 'flex',
            flexDirection: 'column',
            rowGap: '6px',
            alignItems: 'start',
           }}
          >
           <div
            className="px-2 py-1"
            style={{ border: '1px solid #bbb', borderRadius: '7px' }}
           >
            Status: <strong>Connected</strong>
           </div>
           <div
            className="px-2 py-1"
            style={{ border: '1px solid #bbb', borderRadius: '7px' }}
           >
            Sync Direction:{' '}
            <strong>
             {syncDirectionLabel[data.sync_direction ?? 'twoway']}
            </strong>
           </div>
           <div
            className="px-2 py-1"
            style={{ border: '1px solid #bbb', borderRadius: '7px' }}
           >
            Last Synced:{' '}
            <strong>
             {data.last_synced_at
              ? moment(data.last_synced_at).format('MMM DD, YYYY h:mm A')
              : 'Never synced'}
            </strong>
           </div>
           <div
            className="d-flex mt-2"
            style={{ columnGap: '5px', flexWrap: 'wrap', rowGap: '5px' }}
           >
            <Button
             onClick={() => {
              setModalState({ mode: 'EDIT', show: true, data })
             }}
             shape="round"
             type="primary"
            >
             Update Settings
            </Button>
            <Button onClick={handleSync} loading={isSyncing} shape="round">
             Sync Now
            </Button>
            <Button
             onClick={handleTestConnection}
             loading={isTesting}
             shape="round"
            >
             Test Connection
            </Button>
            <Button onClick={handleDisconnect} shape="round" danger>
             Disconnect
            </Button>
           </div>
          </div>
         </div>
        ) : (
         <div className="p-4">
          <h2>Configure Follow Up Boss Account</h2>
          <ol className="ps-4">
           <li>Login to your Follow Up Boss Dashboard</li>
           <li>Go to Admin → API and copy your API Key</li>
           <li>Choose your preferred sync direction</li>
           <li>Paste the details and connect</li>
          </ol>
          <Button
           onClick={() => {
            setModalState({ mode: 'ADD', show: true })
           }}
           shape="round"
           type="primary"
          >
           Connect Follow Up Boss
          </Button>
         </div>
        )}
       </Col>
       <div className="intgr-modal-video">
        <video controls>
         <source
          src="https://cdn.agentroof.com/crm-tutorial/fub-integration.mp4"
          type="video/mp4"
         />
         Your browser does not support the video tag.
        </video>
       </div>
      </Row>
     </div>
    </div>
    <hr className="mt-4" style={{ border: '2px solid #aaa', width: '100%' }} />
    <div className="intgr-modal-bottom">
     <p>
      Enhance your lead engagement by integrating your CRM with Follow Up Boss.
      With this powerful integration, you can sync leads and contacts directly
      between AgentRoof CRM and Follow Up Boss, ensuring faster responses,
      better follow-ups, and more meaningful conversations with your leads.
     </p>
     <h4>Key Features:</h4>
     <ul>
      <li>
       <strong>Two-Way Sync:</strong> Leads and contacts stay up to date in both
       CRMs automatically — no manual exports needed.
      </li>
      <li>
       <strong>Automatic Lead Linking: </strong> Contacts are matched by email
       or phone across both platforms, preventing duplicate records.
      </li>
      <li>
       <strong>Flexible Sync Direction: </strong> Choose two-way sync, export
       from AgentRoof to Follow Up Boss, or import from Follow Up Boss to
       AgentRoof.
      </li>
      <li>
       <strong>Actionable Insights:</strong> Use lead source and campaign data
       to trigger automated workflows such as Action Plans and Listing Alerts.
      </li>
      <li>
       <strong>Scalable Syncing: </strong> Whether you’re managing a few leads
       or thousands, the Follow Up Boss integration ensures reliable and secure
       data transfer.
      </li>
     </ul>
     <h4>Why Use Follow Up Boss Integration?</h4>
     <p>
      This integration helps you stay connected with your leads in real-time,
      ensuring you never miss an opportunity. By managing your contacts directly
      across both CRMs, you streamline communication, improve response times,
      and build stronger relationships with prospects. Integrating Follow Up
      Boss with AgentRoof CRM is the perfect way to modernize your lead
      management strategy, making every interaction timely, personal, and
      impactful.
     </p>
    </div>
   </div>
   <AddFubIntergrationModal
    editData={modalState.data}
    OnCancel={() => {
     setModalState(initModalState)
    }}
    mode={modalState.mode}
    show={modalState.show}
   />
  </Modal>
 )
}

export default FubIntergrationModal
